import { formatDeviceId, type SessionSettings } from '@viewer/protocol';
import { useEffect, useRef, useState } from 'react';
import { INSIDE_APP } from '../../lib/config';
import type { ActiveSession, RemoteCursorListener } from '../../lib/session/controller';
import { StatusDot } from '../../ui/StatusDot';
import { peerStateLabel, peerStateTone } from '../../ui/peerState';
import { DetachedMonitor } from './DetachedMonitor';
import { MonitorPicker } from './MonitorPicker';
import { closeMonitorWindow, monitorLabel, openMonitorWindow } from './monitors';
import { RemoteScreen, type ScreenZoom } from './RemoteScreen';
import { viewerTransferAccess, type SendInput } from './remoteInput';
import { SessionSettingsMenu } from './SessionSettingsMenu';
import styles from './ViewerScreen.module.css';

interface Props {
  session: ActiveSession;
  sendInput: SendInput;
  watchRemoteCursor(listener: RemoteCursorListener): () => void;
  onChangeSettings(patch: Partial<SessionSettings>): void;
  onChangeVolume(volume: number): void;
  onEnd(): void;
  onSendFiles(files: File[]): void;
  onDropFiles(data: DataTransfer): void;
  onDownloadCopied(): void;
  onCancelTransfer(): void;
  onAppPaste(): void;
  onBrowserPaste(data: DataTransfer): boolean;
}

export function ViewerScreen({
  session,
  sendInput,
  watchRemoteCursor,
  onChangeSettings,
  onChangeVolume,
  onEnd,
  onSendFiles,
  onDropFiles,
  onDownloadCopied,
  onCancelTransfer,
  onAppPaste,
  onBrowserPaste,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null);
  const [fps, setFps] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState<ScreenZoom>('fit');
  // O Host manda o principal primeiro: a sessão abre nele.
  const [shownMonitor, setShownMonitor] = useState<number | null>(0);
  const [detached, setDetached] = useState<Array<{ index: number; popup: Window }>>([]);
  const detachedRef = useRef(detached);

  const monitors = session.screen?.monitors ?? [];
  const multiMonitor = monitors.length > 1;
  const mainMonitor = multiMonitor && shownMonitor !== null ? (monitors[shownMonitor] ?? null) : null;

  // O <audio> toca o som quando "Transmitir áudio" está ligado nesta conexão; o vídeo fica na RemoteScreen.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.srcObject = session.remoteStream;
    if (session.remoteStream) audio.play().catch(() => {});
    return () => {
      audio.srcObject = null;
    };
  }, [session.remoteStream]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = session.volume;
  }, [session.volume]);

  // Dimensões reais do vídeo, para a barra de status.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () => setVideoSize({ width: video.videoWidth, height: video.videoHeight });
    video.addEventListener('loadedmetadata', update);
    video.addEventListener('resize', update);
    return () => {
      video.removeEventListener('loadedmetadata', update);
      video.removeEventListener('resize', update);
    };
  }, []);

  // FPS decodificados, via WebRTC stats.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let last = { frames: 0, time: performance.now() };
    const timer = setInterval(() => {
      const quality = video.getVideoPlaybackQuality();
      const now = performance.now();
      const frames = quality.totalVideoFrames;
      const seconds = (now - last.time) / 1000;
      if (seconds > 0) setFps(Math.round((frames - last.frames) / seconds));
      last = { frames, time: now };
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const canControl = session.info.access === 'control';
  const transfer = viewerTransferAccess(session.info);

  const mouseBlocked = canControl && session.mouseBlocked;
  const blockRequested = canControl && session.inputOpen && session.settings.blockLocalInput;

  // No navegador, só em tela cheia a Keyboard Lock API entrega à página Alt+Tab, a tecla Windows e Ctrl+W.
  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', update);
    return () => {
      document.removeEventListener('fullscreenchange', update);
      if (document.fullscreenElement) void document.exitFullscreen();
    };
  }, []);

  async function toggleFullscreen() {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }
    await rootRef.current?.requestFullscreen();
    await navigator.keyboard?.lock();
    surfaceRef.current?.focus();
  }

  useEffect(() => {
    detachedRef.current = detached;
  }, [detached]);

  // As janelas dos monitores desacoplados fecham junto com a sessão (ou com a página).
  useEffect(() => {
    const closeAll = () => {
      for (const { popup } of detachedRef.current) closeMonitorWindow(popup);
    };
    window.addEventListener('pagehide', closeAll);
    return () => {
      window.removeEventListener('pagehide', closeAll);
      closeAll();
    };
  }, []);

  // Fechar a janela de um monitor pelo X também o reanexa.
  useEffect(() => {
    if (detached.length === 0) return;
    const timer = setInterval(() => {
      setDetached((list) => (list.some(({ popup }) => popup.closed) ? list.filter(({ popup }) => !popup.closed) : list));
    }, 500);
    return () => clearInterval(timer);
  }, [detached.length]);

  // Um monitor desligado no computador remoto leva junto a janela dele.
  useEffect(() => {
    const gone = detached.filter(({ index }) => index >= monitors.length);
    if (gone.length === 0) return;
    for (const { popup } of gone) closeMonitorWindow(popup);
    setDetached((list) => list.filter(({ index }) => index < monitors.length));
  }, [detached, monitors.length]);

  function detachMonitor(index: number) {
    const monitor = monitors[index];
    if (!monitor) return;
    const popup = openMonitorWindow(`${monitorLabel(index, monitor)} · ${session.info.peerName}`, monitor);
    if (!popup) return;
    const open = [...detached.map((item) => item.index), index];
    setDetached([...detached, { index, popup }]);
    if (shownMonitor !== index) return;
    // A janela principal passa para o próximo monitor que ficou nela.
    const next = monitors.findIndex((_, i) => !open.includes(i));
    setShownMonitor(next === -1 ? null : next);
  }

  function reattachMonitor(index: number) {
    const item = detached.find((entry) => entry.index === index);
    setDetached(detached.filter((entry) => entry !== item));
    setShownMonitor(index);
    if (item) closeMonitorWindow(item.popup);
  }

  const resolution = videoSize && videoSize.width > 0 ? `${videoSize.width}×${videoSize.height}` : null;

  return (
    <div ref={rootRef} className={styles.root}>
      <header className={styles.bar}>
        <div className={styles.left}>
          <StatusDot tone={peerStateTone(session.peerState)} />
          <span>{peerStateLabel(session.peerState)}</span>
          <span className={styles.peer}>
            {session.info.peerName} · {formatDeviceId(session.info.peerId)}
          </span>
          {multiMonitor && (
            <MonitorPicker
              monitors={monitors}
              shown={mainMonitor ? shownMonitor : null}
              detached={detached.map((item) => item.index)}
              onShow={setShownMonitor}
              onDetach={detachMonitor}
              onReattach={reattachMonitor}
            />
          )}
        </div>
        <div className={styles.right}>
          {resolution && <span className={styles.metric}>{resolution}</span>}
          {fps !== null && session.peerState === 'connected' && <span className={styles.metric}>{fps} fps</span>}
          {session.rttMs !== null && <span className={styles.metric}>{session.rttMs} ms</span>}
          {session.link && (
            <span
              className={styles.metric}
              title={`local: ${session.link.localType} · remoto: ${session.link.remoteType} · ${session.link.protocol}`}
            >
              {session.link.path === 'relay' ? 'Via servidor TURN' : 'Conexão direta'}
            </span>
          )}
          <span className={styles.metric}>
            {!canControl
              ? 'Somente visualização'
              : !session.inputOpen
                ? 'Sem controle'
                : blockRequested && session.inputBlocked
                  ? 'Usuário local bloqueado'
                  : mouseBlocked
                    ? 'Usuário no controle'
                    : 'Você está no controle'}
          </span>
          {blockRequested && session.inputBlocked === false && (
            <span
              className={styles.warning}
              title="O Viewer precisa estar instalado como serviço (ou rodando como administrador) no computador acessado."
            >
              Bloqueio indisponível
            </span>
          )}
          {canControl && session.transfer && (
            <span className={styles.transfer}>
              <span className={styles.transferName} title={session.transfer.label}>
                {session.transfer.label}
              </span>
              <span>{session.transfer.percent}%</span>
              <button type="button" className={styles.action} onClick={onCancelTransfer}>
                Cancelar
              </button>
            </span>
          )}
          {transfer.downloadFiles && session.remoteFiles && session.remoteFiles.skippedFolders > 0 && (
            <span className={styles.metric}>Pastas ainda não são suportadas.</span>
          )}
          {/* O Chrome só arrasta para fora da página um endereço que já tenha os bytes.
              A cópia é sob demanda, então no navegador o caminho é este botão. */}
          {!INSIDE_APP && transfer.downloadFiles && session.remoteFiles && session.remoteFiles.count > 0 && (
            <button
              type="button"
              className={styles.action}
              disabled={!session.inputOpen}
              onClick={onDownloadCopied}
            >
              Baixar arquivos copiados ({session.remoteFiles.count})
            </button>
          )}
          {transfer.sendFiles && (
            <>
              <input
                ref={fileRef}
                className={styles.hiddenFile}
                type="file"
                multiple
                onChange={(event) => {
                  const files = event.target.files ? [...event.target.files] : [];
                  event.target.value = '';
                  if (files.length > 0) onSendFiles(files);
                }}
              />
              <button
                type="button"
                className={styles.action}
                disabled={!session.inputOpen}
                onClick={() => fileRef.current?.click()}
              >
                Enviar arquivos
              </button>
            </>
          )}
          {canControl && (
            <button
              type="button"
              className={styles.action}
              disabled={!session.inputOpen}
              title="Envia Ctrl+Alt+Del ao computador remoto (tela de bloqueio e de login)"
              onClick={() => sendInput({ type: 'ctrl_alt_del' })}
            >
              Ctrl+Alt+Del
            </button>
          )}
          {!INSIDE_APP && (
            <button
              type="button"
              className={styles.action}
              title="Em tela cheia, atalhos como Alt+Tab, a tecla Windows e Ctrl+W vão para o computador remoto. Segure Esc para sair."
              onClick={() => void toggleFullscreen()}
            >
              {fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            </button>
          )}
          <SessionSettingsMenu
            settings={session.settings}
            canControl={canControl}
            volume={session.volume}
            zoom={zoom}
            onChange={onChangeSettings}
            onVolumeChange={onChangeVolume}
            onZoomChange={setZoom}
          />
          <button className={styles.end} onClick={onEnd}>
            Encerrar
          </button>
        </div>
      </header>

      <RemoteScreen
        session={session}
        monitor={mainMonitor}
        zoom={zoom}
        surfaceRef={surfaceRef}
        videoRef={videoRef}
        sendInput={sendInput}
        watchRemoteCursor={watchRemoteCursor}
        onAppPaste={onAppPaste}
        onBrowserPaste={onBrowserPaste}
        onDragEnter={(event) => {
          if (!transfer.sendFiles || !session.inputOpen) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => {
          if (!transfer.sendFiles || !session.inputOpen) return;
          event.preventDefault();
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!transfer.sendFiles || !session.inputOpen) return;
          onDropFiles(event.dataTransfer);
        }}
      >
        <audio ref={audioRef} autoPlay />
        {dragging && <div className={styles.dropHint}>Solte para enviar à Área de Trabalho</div>}
        {!session.remoteStream && (
          <div className={styles.overlay}>
            <div className={styles.spinner} aria-hidden="true" />
            <p>{session.peerState === 'connected' ? 'Aguardando vídeo...' : 'Estabelecendo conexão...'}</p>
          </div>
        )}
      </RemoteScreen>

      {detached.map(({ index, popup }) => {
        const monitor = monitors[index];
        return (
          monitor && (
            <DetachedMonitor
              key={index}
              popup={popup}
              index={index}
              monitor={monitor}
              zoom={zoom}
              session={session}
              sendInput={sendInput}
              watchRemoteCursor={watchRemoteCursor}
              onAppPaste={onAppPaste}
              onBrowserPaste={onBrowserPaste}
              onReattach={() => reattachMonitor(index)}
            />
          )
        );
      })}
    </div>
  );
}
